<script setup lang="ts">
import { onMounted, ref, useId } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: string
    busy?: boolean
    disabled?: boolean
    hint?: string | null
  }>(),
  { busy: false, disabled: false, hint: null },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  paste: [text: string]
  submit: []
}>()

const inputEl = ref<HTMLInputElement | null>(null)
const fieldId = useId()
const hintId = `${fieldId}-hint`

onMounted(() => {
  inputEl.value?.focus()
})

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}

/**
 * Fires *before* the field value updates. We forward the clipboard text so the
 * composable can skip its debounce; the `input` event that follows carries the
 * authoritative value.
 */
function onPaste(event: ClipboardEvent): void {
  emit('paste', event.clipboardData?.getData('text') ?? '')
}

function clear(): void {
  emit('update:modelValue', '')
  inputEl.value?.focus()
}
</script>

<template>
  <div class="field-wrap">
    <label class="visually-hidden" :for="fieldId">Link do vídeo do YouTube</label>

    <div class="field" :class="{ 'field--busy': props.busy }">
      <span class="field__glyph" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" stroke-linecap="round" />
          <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" stroke-linecap="round" />
        </svg>
      </span>

      <input
        :id="fieldId"
        ref="inputEl"
        class="field__input"
        type="url"
        inputmode="url"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        enterkeyhint="go"
        placeholder="Cole um link do YouTube"
        :value="props.modelValue"
        :disabled="props.disabled"
        :aria-busy="props.busy"
        :aria-describedby="props.hint ? hintId : undefined"
        @input="onInput"
        @paste="onPaste"
        @keydown.enter.prevent="emit('submit')"
      />

      <span v-if="props.busy" class="field__spinner" aria-hidden="true" />
      <button
        v-else-if="props.modelValue !== ''"
        type="button"
        class="field__clear"
        @click="clear"
      >
        <span class="visually-hidden">Limpar o link</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <p v-if="props.hint" :id="hintId" class="field__hint">{{ props.hint }}</p>
  </div>
</template>

<style scoped>
.field-wrap {
  display: grid;
  gap: 0.625rem;
}

.field {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem 0 1.125rem;
  min-height: 4rem;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-lg);
  background:
    linear-gradient(180deg, rgb(255 255 255 / 4%), transparent 60%),
    var(--bg-1);
  transition:
    border-color var(--dur) var(--ease),
    box-shadow var(--dur) var(--ease),
    background-color var(--dur) var(--ease);
}

.field:focus-within {
  border-color: var(--accent-line);
  box-shadow:
    0 0 0 4px var(--accent-soft),
    var(--shadow-accent);
}

.field--busy {
  border-color: var(--accent-line);
}

.field__glyph {
  display: grid;
  place-items: center;
  width: 1.25rem;
  color: var(--text-3);
  transition: color var(--dur) var(--ease);
}

.field:focus-within .field__glyph {
  color: var(--accent);
}

.field__input {
  min-width: 0;
  padding: 1.125rem 0;
  border: 0;
  background: none;
  font-size: var(--step-1);
  font-weight: 500;
  letter-spacing: -0.015em;
  outline: none;
}

.field__input::placeholder {
  color: var(--text-3);
  font-weight: 400;
}

.field__input:disabled {
  opacity: 0.5;
}

.field__clear {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  color: var(--text-3);
  transition:
    color var(--dur) var(--ease),
    background-color var(--dur) var(--ease);
}

.field__clear:hover {
  color: var(--text);
  background: rgb(255 255 255 / 6%);
}

.field__clear svg {
  width: 1rem;
  height: 1rem;
}

.field__spinner {
  width: 1.125rem;
  height: 1.125rem;
  border: 2px solid var(--line-2);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 700ms linear infinite;
}

.field__hint {
  padding-inline: 0.375rem;
  font-size: var(--step--1);
  color: var(--text-3);
}

@media (prefers-reduced-motion: reduce) {
  .field__spinner {
    animation: none;
    border-top-color: var(--accent);
    opacity: 0.8;
  }
}
</style>
